import type { ScorecardPayload } from '../../utils/api-client/api-schemas.ts'
import { toPostureBand, type ScoredPostureBand } from '../../utils/threat-model/scorecard-bands.ts'
import { meanScore } from '../../utils/threat-model/scorecard-score.ts'

/**
 * What the estate's scorecards say collectively, dimension by dimension.
 *
 * The pure half, like {@link file://./org-threat-posture.ts}: given the scorecards that came back, it
 * computes the roll-up and never fetches.
 *
 * **Built on scorecards rather than on the repository risk column, and the two do not convert.** They
 * are not one figure read two ways:
 *
 * - This roll-up is **posture**: 0–1, higher is better, averaged **per dimension** across the estate, so
 *   a dimension can be named as the estate's weakest. No exposure is folded in.
 * - The repositories page's **risk** is 0–100, higher is worse, and per repository: posture inverted
 *   into an uncovered gap, then scaled by how reachable that repository is from an untrusted network.
 *
 * So inverting this number will not reproduce an average of the risk column, and is not meant to.
 * Exposure is per repository and has no meaning averaged into a dimension, while a dimension's mean is
 * the thing this page exists to show — aggregating the risk column would answer a different question and
 * lose which dimension to act on.
 *
 * **One definition of a mean, product-wide.** Both levels use {@link meanScore}, the same function the
 * repository's own Scorecard tab shows its total with: equal weight per dimension, dimensions that could
 * not be scored excluded rather than counted as zero, and `undefined` — not `0` — when nothing is
 * scorable. Re-deriving it here would let the estate headline drift from the number on each repository's
 * page, and the first person to notice would be someone comparing two screens.
 */

export type ScorecardInput = {
  repositoryId: string
  name: string
  scorecard: ScorecardPayload
}

/** One repository below adequate on a dimension, carried so the count can name who it is counting. */
export type WeakRepository = {
  repositoryId: string
  name: string
  score: number
}

export type DimensionPosture = {
  /** Stable rubric id, so a dimension survives a display-name change. */
  dimension: string
  displayName: string
  /** Undefined when no repository could score this dimension — reported as such, never as a zero. */
  meanScore: number | undefined
  scoredRepositoryCount: number
  /**
   * Repositories where this dimension lands below adequate — `fair` or `weak`, so under 0.6.
   *
   * The same reading the coverage columns on this page take: anything short of defended counts. A
   * weak-only figure would be narrower and more alarming, and would leave the 0.4–0.6 band
   * unrepresented anywhere.
   */
  weakRepositoryCount: number
  /**
   * Which repositories those are, worst score first.
   *
   * The count alone is not actionable — "39" names nobody — and the aggregate is the only place that
   * still knows. Ordered here rather than in the view so every consumer gets the same ranking, and held
   * in full rather than pre-truncated so the view owns how many it can fit.
   */
  weakRepositories: WeakRepository[]
  /**
   * How the scored repositories fall across the four bands.
   *
   * The mean says where the middle sits and `weakRepositoryCount` says how many are in trouble; neither
   * says whether a dimension is uniformly mediocre or split between strong and weak repositories. That
   * shape is what the coverage bar draws, and it is free here — every score is already in hand.
   *
   * Scored repositories only. An unscorable dimension is absent from all four, and the difference between
   * their sum and `scoredRepositoryCount` is always zero; the not-scored remainder is a property of the
   * estate, not of this record.
   */
  bandCounts: Record<ScoredPostureBand, number>
  meanConfidence: number | undefined
}

export type OrgScorecardPosture = {
  /**
   * One row per dimension the estate's scorecards mention, in the order the rubric emits them.
   *
   * Not sorted by score: a table that reorders itself because one repository was rescored is harder to
   * read across visits than one whose rows sit still.
   */
  dimensions: DimensionPosture[]
  /**
   * The mean of the per-repository totals — "the average repository", rather than the mean of every
   * dimension score pooled together.
   *
   * Those two differ as soon as repositories score different numbers of dimensions, and this is the one a
   * reader assumes: pooling would quietly weight a thoroughly-scored repository more heavily than a
   * sparsely-scored one.
   */
  estatePosture: number | undefined
  /** Scorecards that answered. The denominator every figure here is measured over. */
  scorecardCount: number
  /**
   * Every rubric version present, sorted.
   *
   * Surfaced rather than swallowed: scores from different rubric versions are not strictly comparable, so
   * a view that averages across them owes the reader that caveat instead of one confident number.
   */
  rubricVersions: string[]
  /** How much the models had to assume, averaged. Absent when no scorecard reported it. */
  meanAssumptionsOverhang: number | undefined
}

const isBelowAdequate = (score: number): boolean => {
  const band = toPostureBand(score)
  return band === 'fair' || band === 'weak'
}

/** Scored values bucketed into the four bands, every band present so a consumer can map the order. */
const countByBand = (scores: number[]): Record<ScoredPostureBand, number> => {
  const counts = { weak: 0, fair: 0, adequate: 0, strong: 0 }
  for (const score of scores) {
    const band = toPostureBand(score)
    if (band !== 'unknown') {
      counts[band] += 1
    }
  }
  return counts
}

export const aggregateOrgScorecards = (scorecards: readonly ScorecardInput[]): OrgScorecardPosture => {
  /**
   * Keyed on the rubric id and ordered by first appearance, so the table follows the rubric rather than
   * an alphabetisation of display names that would scatter related dimensions.
   */
  const byDimension = new Map<
    string,
    { displayName: string; scores: number[]; confidences: number[]; weak: WeakRepository[] }
  >()

  for (const { repositoryId, name, scorecard } of scorecards) {
    for (const dimension of scorecard.dimensions) {
      const existing = byDimension.get(dimension.dimension) ?? {
        displayName: dimension.displayName,
        scores: [],
        confidences: [],
        weak: [],
      }
      existing.scores.push(dimension.score)
      existing.confidences.push(dimension.confidence)
      if (Number.isFinite(dimension.score) && isBelowAdequate(dimension.score)) {
        existing.weak.push({ repositoryId, name, score: dimension.score })
      }
      byDimension.set(dimension.dimension, existing)
    }
  }

  const dimensions: DimensionPosture[] = [...byDimension.entries()].map(([dimension, entry]) => {
    const scorable = entry.scores.filter(score => Number.isFinite(score))
    return {
      dimension,
      displayName: entry.displayName,
      meanScore: meanScore(entry.scores),
      scoredRepositoryCount: scorable.length,
      weakRepositoryCount: scorable.filter(isBelowAdequate).length,
      bandCounts: countByBand(scorable),
      // Worst first, then by name so two repositories on the same score keep a stable order across renders.
      weakRepositories: [...entry.weak].sort((a, b) => a.score - b.score || a.name.localeCompare(b.name)),
      meanConfidence: meanScore(entry.confidences),
    }
  })

  return {
    dimensions,
    estatePosture: meanScore(
      // Each repository contributes its own total, not its individual dimensions.
      scorecards
        .map(({ scorecard }) => meanScore(scorecard.dimensions.map(dimension => dimension.score)))
        .filter((total): total is number => total !== undefined)
    ),
    scorecardCount: scorecards.length,
    rubricVersions: [
      ...new Set(scorecards.flatMap(({ scorecard }) => scorecard.dimensions.map(d => d.rubricVersion))),
    ].sort(),
    meanAssumptionsOverhang: meanScore(scorecards.map(({ scorecard }) => scorecard.assumptionsOverhang)),
  }
}
